// Jeu de données de « découverte » semé au premier login d'un NOUVEAU compte,
// quand l'utilisateur coche l'option dans le wizard d'onboarding. Objectif :
// qu'il arrive sur un tableau de bord PEUPLÉ (articles, tiers, quelques
// documents) et comprenne le flux par l'exemple, plutôt que sur des écrans
// vides.
//
// Périmètre volontairement NON FISCAL (décision produit, option B) :
//   — référentiel : articles, clients, fournisseurs ;
//   — documents : DEVIS et COMMANDES uniquement.
// On ne sème JAMAIS de facture, de BL ni d'avoir. Ces documents portent la
// numérotation légale sans trou (Tunisie) et ne se suppriment pas librement :
// injecter de fausses factures dans les livres d'un vrai client décalerait sa
// première vraie facture et créerait un trou impossible à combler. Devis et
// commandes sont commerciaux, librement supprimables, et une éventuelle
// discontinuité de leur série n'a aucune conséquence légale — l'utilisateur
// efface les exemples quand il veut sans rien casser.
//
// Les DEVIS ET COMMANDES sont AUTONOMES (pas de filiation devis → commande).
// La transformation atomique côté serveur (`create_sales_quote_order_atomic`)
// exige qu'une commande rattachée à un devis référence, ligne par ligne, les
// lignes source du devis (`source_doc_id` / `source_line_id`) — sinon elle est
// rejetée (`sales_order_parent_without_lineage`). Reproduire fidèlement cette
// filiation dans un semis demande un test d'intégration ; tant qu'il n'existe
// pas, on n'émet aucun `parentDocId` ici (une démo de transformation reste un
// enrichissement possible plus tard).
//
// Ce fichier est PUR (règle libs/domain) : il ne touche jamais la base. Les
// identifiants d'articles et de clients arrivent déjà résolus (le semis les a
// créés) via `DemoSalesBuildContext`. Les totaux ne sont pas « recalculés pour
// la démo » : on appelle `calculateDocumentTotals` de @erp/domain/sales —
// exactement la fonction du formulaire de vente — pour que les documents
// d'exemple soient indistinguables de vrais documents.

import {
  calculateLineTotals,
  calculateDocumentTotals,
} from '@erp/domain/sales';
import type { DocumentLineInput, DocumentTotals } from '@erp/domain/sales';

export type DemoThirdPartyType = 'company' | 'individual';

/** Un article du mini-catalogue d'exemple. Prix en dinars (millimes). Le TTC
 *  est DÉRIVÉ du HT au moment du semis (voir seed-demo-data.ts) pour ne pas
 *  dupliquer un prix qui pourrait diverger. */
export interface DemoArticleSeed {
  /** Clé locale de résolution, jamais persistée. */
  ref: string;
  name: string;
  unit: string;
  vatRate: number;
  purchasePriceHt: number;
  salePriceHt: number;
  family?: string;
  brand?: string;
}

/** Un client ou un fournisseur d'exemple. */
export interface DemoThirdPartySeed {
  ref: string;
  name: string;
  type: DemoThirdPartyType;
  taxId?: string;
  city?: string;
  phone?: string;
  email?: string;
}

export interface DemoSalesLineSeed {
  articleRef: string;
  quantity: number;
  /** Remise ligne en % (défaut 0). */
  discountPct?: number;
}

export interface DemoSalesDocSeed {
  ref: string;
  docType: 'quote' | 'order';
  clientRef: string;
  /** Ancienneté en jours vs « aujourd'hui » — étale les documents sur ~1 mois
   *  pour que les KPI et graphes du tableau de bord aient du relief. */
  daysAgo: number;
  /** Validité (jours après la date du document) — devis uniquement. */
  validityDays?: number;
  lines: DemoSalesLineSeed[];
}

/* ----------------------- Le jeu de données ----------------------- */
// VOLONTAIREMENT GÉNÉRIQUE : aucun secteur d'activité. « Article démo 1 »,
// « Client démo 2 »… parlent autant à un plombier qu'à un libraire, et
// l'utilisateur renomme ce qu'il décide de garder. Les libellés restent en
// français (langue de saisie) ; l'UI, elle, est traduite.
//
// ⚠ Aucune fiche semée ici n'est le client PAR DÉFAUT. Le « Passager » — le
// walk-in de la caisse, qui porte aussi les factures groupées multi-clients
// (voir apps/erp-web/src/lib/default-client.ts) — vit en dehors de ce jeu de
// données : le semis se contente d'AJOUTER des fiches, il n'en supprime
// aucune et n'en marque aucune `isDefault`, donc il ne peut jamais évincer
// ni remplacer le Passager.

export const DEMO_ARTICLES: readonly DemoArticleSeed[] = [
  {
    ref: 'a1',
    name: 'Article démo 1',
    unit: 'pcs',
    vatRate: 19,
    purchasePriceHt: 60,
    salePriceHt: 90,
  },
  {
    ref: 'a2',
    name: 'Article démo 2',
    unit: 'pcs',
    vatRate: 19,
    purchasePriceHt: 8,
    salePriceHt: 12,
  },
  {
    ref: 'a3',
    name: 'Article démo 3',
    unit: 'pcs',
    vatRate: 19,
    purchasePriceHt: 4,
    salePriceHt: 6.5,
  },
  {
    ref: 'a4',
    name: 'Article démo 4',
    unit: 'pcs',
    vatRate: 19,
    purchasePriceHt: 3,
    salePriceHt: 5,
  },
  {
    ref: 'a5',
    name: 'Article démo 5',
    unit: 'pcs',
    vatRate: 19,
    purchasePriceHt: 30,
    salePriceHt: 45,
  },
  {
    ref: 'a6',
    name: 'Article démo 6',
    unit: 'pcs',
    vatRate: 19,
    purchasePriceHt: 2,
    salePriceHt: 3.5,
  },
  {
    ref: 'a7',
    name: 'Article démo 7',
    unit: 'pcs',
    vatRate: 19,
    purchasePriceHt: 25,
    salePriceHt: 38,
  },
  {
    ref: 'a8',
    name: 'Article démo 8',
    unit: 'pcs',
    vatRate: 19,
    purchasePriceHt: 5,
    salePriceHt: 8,
  },
];

// Un mélange entreprise / particulier : c'est ce qui rend la règle de retenue
// à la source visible (elle ne vise que les entreprises).
export const DEMO_CLIENTS: readonly DemoThirdPartySeed[] = [
  {
    ref: 'c1',
    name: 'Client démo 1',
    type: 'company',
    taxId: '0000001/A/M/000',
  },
  {
    ref: 'c2',
    name: 'Client démo 2',
    type: 'company',
    taxId: '0000002/A/M/000',
  },
  {
    ref: 'c3',
    name: 'Client démo 3',
    type: 'company',
    taxId: '0000003/A/M/000',
  },
  { ref: 'c4', name: 'Client démo 4', type: 'individual' },
  { ref: 'c5', name: 'Client démo 5', type: 'individual' },
];

export const DEMO_SUPPLIERS: readonly DemoThirdPartySeed[] = [
  {
    ref: 's1',
    name: 'Fournisseur démo 1',
    type: 'company',
    taxId: '0000101/A/M/000',
  },
  {
    ref: 's2',
    name: 'Fournisseur démo 2',
    type: 'company',
    taxId: '0000102/A/M/000',
  },
  {
    ref: 's3',
    name: 'Fournisseur démo 3',
    type: 'company',
    taxId: '0000103/A/M/000',
  },
];

export const DEMO_SALES_DOCS: readonly DemoSalesDocSeed[] = [
  // Trois devis étalés sur le mois.
  {
    ref: 'q1',
    docType: 'quote',
    clientRef: 'c1',
    daysAgo: 26,
    validityDays: 30,
    lines: [
      { articleRef: 'a1', quantity: 2 },
      { articleRef: 'a2', quantity: 5 },
    ],
  },
  {
    ref: 'q2',
    docType: 'quote',
    clientRef: 'c4',
    daysAgo: 18,
    validityDays: 30,
    lines: [
      { articleRef: 'a6', quantity: 20 },
      { articleRef: 'a4', quantity: 10 },
    ],
  },
  {
    ref: 'q3',
    docType: 'quote',
    clientRef: 'c2',
    daysAgo: 9,
    validityDays: 30,
    lines: [
      { articleRef: 'a5', quantity: 1 },
      { articleRef: 'a7', quantity: 3 },
      { articleRef: 'a2', quantity: 8 },
    ],
  },
  // Deux commandes autonomes (voir l'en-tête : pas de filiation dans le semis).
  {
    ref: 'o1',
    docType: 'order',
    clientRef: 'c1',
    daysAgo: 21,
    lines: [
      { articleRef: 'a1', quantity: 2 },
      { articleRef: 'a2', quantity: 5 },
    ],
  },
  // Une remise de ligne, pour que l'exemple montre aussi une remise.
  {
    ref: 'o2',
    docType: 'order',
    clientRef: 'c3',
    daysAgo: 6,
    lines: [
      { articleRef: 'a1', quantity: 5, discountPct: 5 },
      { articleRef: 'a7', quantity: 4 },
      { articleRef: 'a8', quantity: 3 },
    ],
  },
];

/* ----------------------- Construction des documents ----------------------- */

export interface DemoBuiltLine {
  articleId: string;
  description: string;
  quantity: number;
  unitPriceHt: number;
  discountPct: number;
  discountMode: 'pct';
  vatRate: number;
  purchasePrice: number;
  totalHt: number;
  totalTtc: number;
}

export interface DemoBuiltSalesDoc {
  ref: string;
  docType: 'quote' | 'order';
  clientId: string;
  /** ISO `YYYY-MM-DD`. */
  docDate: string;
  /** ISO `YYYY-MM-DD` — devis uniquement. */
  validityDate?: string;
  lines: DemoBuiltLine[];
  totals: DocumentTotals;
}

export interface DemoSalesBuildContext {
  /** Date de référence — injectée pour garder la fonction pure et testable. */
  today: Date;
  articleIdByRef: Readonly<Record<string, string>>;
  clientIdByRef: Readonly<Record<string, string>>;
  clientTypeByRef: Readonly<Record<string, DemoThirdPartyType>>;
}

const ARTICLE_BY_REF: ReadonlyMap<string, DemoArticleSeed> = new Map(
  DEMO_ARTICLES.map((a) => [a.ref, a]),
);

/**
 * Transforme le jeu de données en documents de vente prêts à persister, une
 * fois les articles et clients créés (leurs ids arrivent via `ctx`).
 *
 * Totaux calculés par la MÊME fonction que le formulaire — et, comme pour toute
 * vente, SANS config de retenue à la source : un document de vente reste au TTC
 * plein tant qu'aucun certificat RAS n'est émis.
 */
export function buildDemoSalesDocuments(
  ctx: DemoSalesBuildContext,
): DemoBuiltSalesDoc[] {
  return DEMO_SALES_DOCS.map((doc) => {
    const clientId = required(
      ctx.clientIdByRef[doc.clientRef],
      `client "${doc.clientRef}"`,
    );
    const clientType = ctx.clientTypeByRef[doc.clientRef] ?? 'individual';

    const lines: DemoBuiltLine[] = doc.lines.map((line) => {
      const article = required(
        ARTICLE_BY_REF.get(line.articleRef),
        `article "${line.articleRef}"`,
      );
      const articleId = required(
        ctx.articleIdByRef[line.articleRef],
        `id d'article "${line.articleRef}"`,
      );
      const discountPct = line.discountPct ?? 0;
      const lineTotals = calculateLineTotals({
        quantity: line.quantity,
        unitPriceHt: article.salePriceHt,
        discountPct,
        vatRate: article.vatRate,
      });
      return {
        articleId,
        description: article.name,
        quantity: line.quantity,
        unitPriceHt: article.salePriceHt,
        discountPct,
        discountMode: 'pct',
        vatRate: article.vatRate,
        purchasePrice: article.purchasePriceHt,
        totalHt: lineTotals.totalHt,
        totalTtc: lineTotals.totalTtc,
      };
    });

    const totals = calculateDocumentTotals(
      lines.map<DocumentLineInput>((l) => ({
        quantity: l.quantity,
        unitPriceHt: l.unitPriceHt,
        discountPct: l.discountPct,
        vatRate: l.vatRate,
      })),
      clientType,
    );

    const docDate = isoDateMinusDays(ctx.today, doc.daysAgo);
    const validityDate =
      doc.validityDays != null
        ? isoDateMinusDays(ctx.today, doc.daysAgo - doc.validityDays)
        : undefined;

    return {
      ref: doc.ref,
      docType: doc.docType,
      clientId,
      docDate,
      ...(validityDate ? { validityDate } : {}),
      lines,
      totals,
    };
  });
}

function required<T>(value: T | undefined | null, what: string): T {
  if (value == null) {
    throw new Error(`jeu de données de démo : référence introuvable — ${what}`);
  }
  return value;
}

/** Date ISO (UTC, `YYYY-MM-DD`) décalée de `days` jours dans le passé. */
export function isoDateMinusDays(today: Date, days: number): string {
  const base = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  const shifted = new Date(base);
  shifted.setUTCDate(shifted.getUTCDate() - days);
  return shifted.toISOString().slice(0, 10);
}
